import { Fragment } from 'react';

import { costValueColor, cssVar } from '@/design-system';
import { COST_KEYS, type CostKey, type PublicReportChapter } from '@/entities/publicReport';

import { JudgementPill } from './JudgementPill';

type GuideChapter = Extract<PublicReportChapter, { kind: 'guide' }>;

export interface ReadingGuideProps {
    chapter: GuideChapter;
    /** Het niveau van de tussenkoppen: één onder de kop van het hoofdstuk. */
    level: 3 | 4;
}

/**
 * "Zo leest u dit rapport": wat de drie kostenblokken en de oordelen betekenen,
 * vóór de koper het eerste bedrag en het eerste oordeel tegenkomt. Dezelfde
 * woorden als in de PDF; ze komen van de API.
 *
 * Elk oordeel staat in hetzelfde gekleurde label als bij de koppen verderop, en
 * elk kostenblok in de kleur van zijn bedrag, zodat de koper hier leert wat een
 * kleur betekent.
 */
export function ReadingGuide({ chapter, level }: ReadingGuideProps) {
    const Heading = `h${level}` as const;
    const subheading =
        'm-0 mb-3 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]';

    return (
        <div className="flex flex-col gap-6">
            {chapter.costs ? (
                <section>
                    <Heading className={subheading}>{chapter.costs.title}</Heading>
                    <dl className="m-0 grid grid-cols-[minmax(96px,max-content)_minmax(0,1fr)] gap-x-4 gap-y-2">
                        {chapter.costs.items.map((item) => (
                            <Fragment key={item.key}>
                                <dt
                                    className="text-[15px] leading-[1.6] font-semibold"
                                    style={{
                                        color: isCostKey(item.key)
                                            ? costValueColor[item.key]
                                            : cssVar.textPrimary,
                                    }}
                                >
                                    {item.label}
                                </dt>
                                <dd className="m-0 text-[15px] leading-[1.6] text-pretty text-[var(--text-reading)]">
                                    {item.text}
                                </dd>
                            </Fragment>
                        ))}
                    </dl>
                </section>
            ) : null}

            <section>
                <Heading className={subheading}>{chapter.judgements.title}</Heading>
                {chapter.judgements.note ? (
                    <p className="m-0 mb-3 text-[14px] leading-[1.55] text-[var(--text-tertiary-safe)]">
                        {chapter.judgements.note}
                    </p>
                ) : null}
                <dl className="m-0 grid grid-cols-[minmax(96px,max-content)_minmax(0,1fr)] items-baseline gap-x-4 gap-y-[10px]">
                    {chapter.judgements.items.map((item) => (
                        <Fragment key={item.key}>
                            <dt className="m-0">
                                <JudgementPill label={item.label} tone={item.tone} />
                            </dt>
                            <dd className="m-0 text-[15px] leading-[1.6] text-pretty text-[var(--text-reading)]">
                                {item.text}
                            </dd>
                        </Fragment>
                    ))}
                </dl>
            </section>
        </div>
    );
}

function isCostKey(value: string): value is CostKey {
    return (COST_KEYS as readonly string[]).includes(value);
}
